import React, { useState, useMemo } from "react";
import { Icon, Modal, Tooltip } from "antd";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";

import { selectAdData } from "../../../routes/AdForm/adForm.ducks";
import { CategoryName, ModuleName } from "../../../models";
import { api, parsePriceString } from "../../../services";
import { mapCategoryToEn, mapModuleToEn } from "../../../routes/LanguageMapper";
import styles from "./averagePriceButton.scss";

interface AveragePriceResult {
  averagePrice: number | null;
  medianPrice: number | null;
  p25Price: number | null;
  p75Price: number | null;
  adCount: number | null;
}

const emptyResult: AveragePriceResult = {
  averagePrice: null,
  medianPrice: null,
  p25Price: null,
  p75Price: null,
  adCount: null
};

const AveragePriceButton = () => {
  const selectedData = useSelector(selectAdData);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(emptyResult);
  const [showModal, setShowModal] = useState(false);

  const isCar = useMemo(() => {
    if (!selectedData || !selectedData.module || !selectedData.category) return false;
    return (
      mapModuleToEn(selectedData.module) === ModuleName.vehicles &&
      mapCategoryToEn(selectedData.category, mapModuleToEn(selectedData.module)) === CategoryName.car
    );
  }, [selectedData]);

  if (!isCar) return null;

  const hasRequiredFields = selectedData.makeId && selectedData.modelId && selectedData.makeYear;

  // Exclude: isNew = 4 (Za dijelove), damaged = 1 or 2 (Oštećeno)
  const isExcludedCondition = selectedData &&
    (selectedData.isNew === 4 || selectedData.damaged === 1 || selectedData.damaged === 2);

  const trackNoResult = () => {
    ReactGA.event({
      category: "Predaja oglasa",
      action: "Click",
      label: "Izračun prosječne cijene - bez rezultata"
    });
  };

  const handleClick = async () => {
    // Show fallback immediately for excluded conditions (damaged/for parts vehicles)
    if (isExcludedCondition) {
      trackNoResult();
      setResult(emptyResult);
      setShowModal(true);
      return;
    }

    setIsLoading(true);
    setResult(emptyResult);

    ReactGA.event({
      category: "Predaja oglasa",
      action: "Click",
      label: "Izračun prosječne cijene"
    });

    try {
      const res = await api.Ads.averagePriceCar({
        makeId: selectedData.makeId,
        modelId: selectedData.modelId,
        makeYear: selectedData.makeYear.substring(0, 4)
      });

      const hasData = res.averagePrice !== null || res.medianPrice !== null ||
        (res.p25Price !== null && res.p75Price !== null);

      if (!hasData) {
        trackNoResult();
      }

      setResult(res);
      setShowModal(true);
    } catch (e) {
      console.error("[AveragePrice] Error:", e);
      trackNoResult();
      setResult(emptyResult);
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: number | null) =>
    price === null ? "" : parsePriceString(Math.round(price)).replace(" ", "");

  const hasRange = result.p25Price !== null && result.p75Price !== null;
  const hasAverage = result.averagePrice !== null;
  const hasMedian = result.medianPrice !== null;
  const hasAnyData = hasRange || hasAverage || hasMedian;

  return (
    <React.Fragment>
      <Tooltip
        title={!hasRequiredFields ? "Prvo odaberite marku, model i godište" : ""}
        placement="top"
      >
        <button
          type="button"
          className={`${styles.button} ${!hasRequiredFields ? styles.notReady : ""}`}
          onClick={hasRequiredFields && !isLoading ? handleClick : undefined}
        >
          {isLoading ? <Icon type="loading" /> : <Icon type="bar-chart" />}
          <span className={styles.buttonLabel}>Vidi prosječnu cijenu</span>
        </button>
      </Tooltip>
      <Modal
        visible={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
        className={styles.modal}
        title={null}
        closable={true}
      >
        {hasAnyData && (
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <Icon type="bar-chart" className={styles.modalIcon} />
              <span>{"Cijene sličnih vozila"}</span>
            </div>

            {hasRange && (
              <p className={styles.rangeText}>
                {"Većina sličnih vozila oglašena je između "}
                <strong>{formatPrice(result.p25Price)}</strong>
                {" i "}
                <strong>{formatPrice(result.p75Price)}</strong>
              </p>
            )}

            {(hasAverage || hasMedian) && (
              <div className={styles.priceBlock}>
                {hasAverage && (
                  <div className={styles.priceRow}>
                    <span className={styles.priceLabel}>
                      {"Prosječna cijena"}
                      <Tooltip
                        title="Prosječna cijena je aritmetička sredina svih sličnih oglasa. Može biti osjetljiva na vrlo niske ili visoke cijene."
                        placement="top"
                      >
                        <Icon type="info-circle" className={styles.infoIcon} />
                      </Tooltip>
                    </span>
                    <span className={styles.priceValue}>{formatPrice(result.averagePrice)}</span>
                  </div>
                )}

                {hasMedian && (
                  <div className={styles.priceRow}>
                    <span className={styles.priceLabel}>
                      {"Medijan cijena"}
                      <Tooltip
                        title="Medijan cijena predstavlja srednju vrijednost – polovica oglasa ima nižu, a polovica višu cijenu. Manje je osjetljiva na ekstremne vrijednosti."
                        placement="top"
                      >
                        <Icon type="info-circle" className={styles.infoIcon} />
                      </Tooltip>
                    </span>
                    <span className={styles.priceValue}>{formatPrice(result.medianPrice)}</span>
                  </div>
                )}
              </div>
            )}

            <div className={styles.modalFooter}>
              <p className={styles.adCount}>
                {`Izračunato na temelju ${result.adCount} sličnih oglasa aktivnih u posljednjih 6 mjeseci. U obzir su uzeti oglasi iste marke, modela i godišta.`}
              </p>
              <p className={styles.modalNote}>
                {"Napomena: podatak o cijeni vidljiv je samo tebi tijekom predaje oglasa."}
              </p>
            </div>
          </div>
        )}
        {!hasAnyData && (
          <div className={styles.modalFallback}>
            <Icon type="exclamation-circle" className={styles.fallbackIcon} />
            <p>{"Trenutno nemamo dovoljno sličnih oglasa za izračun cijene."}</p>
          </div>
        )}
      </Modal>
    </React.Fragment>
  );
};

export default AveragePriceButton;



// WEBPACK FOOTER //
// ./src/components/FormControls/Money/AveragePriceButton.tsx